import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useOrgStore } from '@/stores/orgStore';

export type SubscriptionWarningLevel = 'NONE' | 'APPROACHING' | 'FINAL_WEEK' | 'LAPSED';
export type SubscriptionWarningKind = 'TRIAL' | 'SUBSCRIPTION';

export interface SubscriptionWarning {
  level: SubscriptionWarningLevel;
  kind: SubscriptionWarningKind;
  deadline: string | null;
  daysRemaining: number | null;
}

const NO_WARNING: SubscriptionWarning = {
  level: 'NONE',
  kind: 'TRIAL',
  deadline: null,
  daysRemaining: null,
};

/**
 * How close this organisation is to losing access, and why. The escalation is decided in
 * the database so the reminder emails and these screens cannot disagree about whether
 * somebody is a week away from being cut off.
 */
export function useSubscriptionWarning() {
  const orgId = useOrgStore((state) => state.organization?.id);

  return useQuery({
    queryKey: ['subscription-warning', orgId],
    enabled: !!orgId,
    // The deadline moves by the hour, not the minute; a stale banner is worse than a
    // slightly old one is useful.
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SubscriptionWarning> => {
      const { data, error } = await supabase
        .from('organization_subscription_warning')
        .select('level, kind, deadline, days_remaining')
        .eq('organization_id', orgId!)
        .maybeSingle();

      // A frontend deployed ahead of its migration should warn about nothing rather than
      // breaking the workspace shell it is mounted in.
      if (error) {
        if (error.code === '42P01' || error.code === 'PGRST205') return NO_WARNING;
        throw error;
      }
      if (!data) return NO_WARNING;

      return {
        level: data.level as SubscriptionWarningLevel,
        kind: data.kind as SubscriptionWarningKind,
        deadline: data.deadline,
        daysRemaining: data.days_remaining,
      };
    },
  });
}
